import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { cn } from '../index';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeMap = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Prefer the first form field over header chrome (e.g. close button). */
function getInitialFocusTarget(container: HTMLElement): HTMLElement | null {
  return (
    container.querySelector<HTMLElement>(
      'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
    ) ??
    container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
  );
}

export const Modal: React.FC<ModalProps> = ({
  open, onClose, title, description, children, className, size = 'md',
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;

    previousFocus.current = document.activeElement as HTMLElement;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
      if (e.key === 'Tab' && dialogRef.current) {
        const focusables = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Defer so the dialog content is mounted before focusing.
    const focusTimer = window.setTimeout(() => {
      if (dialogRef.current) {
        getInitialFocusTarget(dialogRef.current)?.focus();
      }
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      previousFocus.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        className="fixed inset-0 bg-[var(--overlay)] backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={dialogRef}
        className={cn(
          'relative z-10 w-full rounded-xl bg-[var(--card)] border border-[var(--border)] shadow-xl',
          'max-h-[min(90dvh,720px)] flex flex-col',
          sizeMap[size],
          className,
        )}
      >
        {title && (
          <div className="flex items-start justify-between gap-3 px-4 py-3.5 border-b border-[var(--border)] flex-shrink-0">
            <div className="min-w-0">
              <h2 id="modal-title" className="text-base font-medium text-[var(--fg)]">{title}</h2>
              {description && <p className="text-sm text-[var(--fg-muted)] mt-0.5">{description}</p>}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-[var(--fg-muted)] hover:bg-[var(--muted)] transition-colors flex-shrink-0"
              aria-label="Close dialog"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="p-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
};
